import {
  IMultiplePriceChangeResult,
  IMultiplePriceChangeRule,
  IMultiplePriceChangeRulesDocument,
  IMultiplePriceChangeRulesStoreSettings,
  IMultiplePriceCondition,
  IMultiplePriceGpAction,
  MULTIPLE_PRICE_CHANGE_PRICE_INDEXES,
  TMultiplePriceConditionOperator,
  TMultiplePriceConditionSource,
  TMultiplePriceGpMode,
} from '../../../features/settings-new/models/stock-manager/multiple-price-change-rules.interface';
import {IPriceBand} from '../../shared-models/price-banding/price-band';
import {VatRates} from '../../../shared-utilities/models-old/datastructures';
import {snapPriceToPriceBands} from './price-banding.utils';
import {calculateGPValue} from '../calculations/calculations-stock';

const MAX_GP_PERCENTAGE = 99.99;

/**
 * Highest GP percentage a derived price may target. Anything at or above 100% has no finite price.
 */
export const MULTIPLE_PRICE_CHANGE_MAX_GP = MAX_GP_PERCENTAGE;

/**
 * Firestore field name holding the VAT-inclusive value for a derived price.
 */
export function getInclusivePriceField(priceIndex: number): string {
  return `sellPriceIncluding${priceIndex}`;
}

/**
 * Firestore field name holding the VAT-exclusive value for a derived price.
 */
export function getExclusivePriceField(priceIndex: number): string {
  return `sellPrice${priceIndex}`;
}

/**
 * Every stock item field a multiple price change rule can write to.
 */
export function getMultiplePriceChangeFields(): string[] {
  return MULTIPLE_PRICE_CHANGE_PRICE_INDEXES.reduce((fields: string[], priceIndex: number) => {
    fields.push(getExclusivePriceField(priceIndex), getInclusivePriceField(priceIndex));
    return fields;
  }, []);
}

/**
 * VAT-inclusive price fields only. IQ derives the exclusive value from these, so the stock update
 * message carries the inclusive prices and leaves the exclusive ones out.
 */
export function getMultiplePriceChangeInclusiveFields(): string[] {
  return MULTIPLE_PRICE_CHANGE_PRICE_INDEXES.map((priceIndex: number): string => getInclusivePriceField(priceIndex));
}

export function createEmptyMultiplePriceGpAction(mode: TMultiplePriceGpMode = 'none'): IMultiplePriceGpAction {
  return {mode, priceBanding: false, value: 0};
}

export function createDefaultMultiplePriceChangeRule(priceIndex: number): IMultiplePriceChangeRule {
  return {
    condition: {operator: 'always', source: 'priceOneGp', value: 0},
    enabled: true,
    priceIndex,
    whenFalse: createEmptyMultiplePriceGpAction('none'),
    whenTrue: createEmptyMultiplePriceGpAction('variable'),
  };
}

export function createDefaultMultiplePriceChangeSettings(): IMultiplePriceChangeRulesStoreSettings {
  return {enabled: false, rules: [], source: 'default'};
}

/**
 * Returns true when the rule set can change at least one price.
 */
export function hasActiveMultiplePriceChangeRules(settings: IMultiplePriceChangeRulesStoreSettings | undefined): boolean {
  if (!settings?.enabled) {
    return false;
  }
  return (settings.rules ?? []).some((rule: IMultiplePriceChangeRule) =>
    rule.enabled && (rule.whenTrue?.mode !== 'none' || rule.whenFalse?.mode !== 'none'),
  );
}

/**
 * Converts the Firestore index-keyed document into a stable, ordered rule array.
 */
export function normalizeMultiplePriceChangeRulesDocument(
  data: IMultiplePriceChangeRulesDocument | undefined | null,
): IMultiplePriceChangeRulesStoreSettings {
  if (!data) {
    return createDefaultMultiplePriceChangeSettings();
  }

  const rules: IMultiplePriceChangeRule[] = Object.keys(data.rules ?? {})
    .map((key: string): number => Number(key))
    .filter((priceIndex: number): boolean => MULTIPLE_PRICE_CHANGE_PRICE_INDEXES.includes(priceIndex))
    .sort((left: number, right: number): number => left - right)
    .map((priceIndex: number): IMultiplePriceChangeRule => {
      const stored = data.rules[String(priceIndex)];
      return {
        condition: normalizeCondition(stored?.condition),
        enabled: stored?.enabled ?? false,
        priceIndex,
        whenFalse: normalizeAction(stored?.whenFalse),
        whenTrue: normalizeAction(stored?.whenTrue),
      };
    });

  return {enabled: !!data.enabled, rules, source: 'store'};
}

/**
 * Converts the editor state back into the Firestore index-keyed document shape.
 */
export function toMultiplePriceChangeRulesDocument(
  settings: IMultiplePriceChangeRulesStoreSettings,
): IMultiplePriceChangeRulesDocument {
  const rules: IMultiplePriceChangeRulesDocument['rules'] = {};
  (settings.rules ?? []).forEach((rule: IMultiplePriceChangeRule): void => {
    rules[String(rule.priceIndex)] = {
      condition: normalizeCondition(rule.condition),
      enabled: !!rule.enabled,
      whenFalse: normalizeAction(rule.whenFalse),
      whenTrue: normalizeAction(rule.whenTrue),
    };
  });

  return {enabled: !!settings.enabled, rules, updatedAt: Date.now()};
}

/**
 * Validation errors that must block a save.
 */
export function validateMultiplePriceChangeRules(settings: IMultiplePriceChangeRulesStoreSettings): string[] {
  const errors: string[] = [];

  (settings.rules ?? []).forEach((rule: IMultiplePriceChangeRule): void => {
    if (!rule.enabled) {
      return;
    }
    if (rule.whenTrue.mode === 'none' && rule.whenFalse.mode === 'none') {
      errors.push(`Price ${rule.priceIndex}: choose a fixed or variable GP for at least one outcome.`);
    }
    if (rule.whenTrue.mode === 'fixed' && (rule.whenTrue.value <= 0 || rule.whenTrue.value >= 100)) {
      errors.push(`Price ${rule.priceIndex}: fixed GP must be between 0% and 100%.`);
    }
    if (rule.whenFalse.mode === 'fixed' && (rule.whenFalse.value <= 0 || rule.whenFalse.value >= 100)) {
      errors.push(`Price ${rule.priceIndex}: fixed GP for the alternative outcome must be between 0% and 100%.`);
    }
  });

  return errors;
}

/**
 * Human readable summary of a rule for collapsed accordion headers and the preview modal.
 */
export function describeMultiplePriceChangeRule(rule: IMultiplePriceChangeRule): string {
  const whenTrue: string = describeMultiplePriceGpAction(rule.whenTrue);
  if (rule.condition.operator === 'always') {
    return whenTrue;
  }
  const source: string = describeConditionSource(rule.condition.source);
  const condition = `when ${source} ${describeConditionOperator(rule.condition.operator)} ${rule.condition.value}%`;
  return `${whenTrue} ${condition}, otherwise ${describeMultiplePriceGpAction(rule.whenFalse).toLowerCase()}`;
}

export function describeConditionSource(source: TMultiplePriceConditionSource): string {
  return source === 'targetGp' ? 'target GP' : 'price 1 GP';
}

export function describeMultiplePriceGpAction(action: IMultiplePriceGpAction): string {
  if (!action || action.mode === 'none') {
    return 'Leave unchanged';
  }
  if (action.mode === 'fixed') {
    return `Fixed GP of ${action.value}%`;
  }
  const sign: string = action.value >= 0 ? '+' : '';
  return `Price 1 GP ${sign}${action.value}%`;
}

export function describeConditionOperator(operator: TMultiplePriceConditionOperator): string {
  switch (operator) {
    case 'greaterThan':
      return 'is greater than';
    case 'greaterThanOrEqual':
      return 'is greater than or equal to';
    case 'lessThan':
      return 'is less than';
    case 'lessThanOrEqual':
      return 'is less than or equal to';
    case 'equalTo':
      return 'equals';
    default:
      return 'always applies';
  }
}

/**
 * GP percentage the condition compares against, based on the source chosen for the rule.
 */
export function resolveMultiplePriceConditionGp(
  condition: IMultiplePriceCondition,
  priceOneGp: number,
  targetGp: number,
): number {
  return condition?.source === 'targetGp' ? targetGp : priceOneGp;
}

/**
 * Evaluates a rule condition against the GP percentage of its chosen source.
 */
export function evaluateMultiplePriceCondition(condition: IMultiplePriceCondition, comparisonGp: number): boolean {
  switch (condition?.operator) {
    case 'greaterThan':
      return comparisonGp > condition.value;
    case 'greaterThanOrEqual':
      return comparisonGp >= condition.value;
    case 'lessThan':
      return comparisonGp < condition.value;
    case 'lessThanOrEqual':
      return comparisonGp <= condition.value;
    case 'equalTo':
      return comparisonGp === condition.value;
    default:
      return true;
  }
}

/**
 * Resolves the VAT percentage for a stock item, falling back to the first configured rate.
 */
export function resolveVatPercentage(vatKey: number, vatRates: VatRates): number {
  const rate: number | undefined = vatRates?.[vatKey]?.vatRate;
  if (rate !== undefined && rate !== null) {
    return rate;
  }
  const first = Object.values(vatRates ?? {})[0];
  return first?.vatRate ?? 0;
}

/**
 * VAT-inclusive price that achieves the requested GP percentage for a given cost.
 */
export function calculateInclusivePriceForGp(latestCost: number, gpPercentage: number, vatPercentage: number): number {
  const cappedGp: number = Math.min(gpPercentage, MAX_GP_PERCENTAGE);
  const gpFraction: number = cappedGp / 100;
  const exclusive: number = latestCost / (1 - gpFraction);
  return exclusive * (1 + vatPercentage / 100);
}

export interface IMultiplePriceChangeCalculationParams {
  latestCost: number;
  sellPriceIncluding1: number;
  settings: IMultiplePriceChangeRulesStoreSettings | undefined;
  vatKey: number;
  vatRates: VatRates;
  previousPrices?: Record<string, number>;
  priceBands?: IPriceBand[];
  /** The item's Target GP % (`recommendedGP`), used by conditions with a `targetGp` source. */
  targetGp?: number;
}

/**
 * Resolves every derived price (2-10) from retail price 1 using the store's rules.
 * Rules whose outcome is `none` are skipped so untouched prices are never written.
 */
export function calculateMultiplePriceChanges(params: IMultiplePriceChangeCalculationParams): IMultiplePriceChangeResult[] {
  const {latestCost, previousPrices, priceBands, sellPriceIncluding1, settings, targetGp, vatKey, vatRates} = params;

  if (!hasActiveMultiplePriceChangeRules(settings) || !sellPriceIncluding1) {
    return [];
  }

  const vatPercentage: number = resolveVatPercentage(vatKey, vatRates);
  const priceOneGp: number = calculateGPValue(vatPercentage, sellPriceIncluding1, latestCost);

  return (settings.rules ?? [])
    .filter((rule: IMultiplePriceChangeRule): boolean => rule.enabled)
    .reduce((results: IMultiplePriceChangeResult[], rule: IMultiplePriceChangeRule) => {
      const comparisonGp: number = resolveMultiplePriceConditionGp(rule.condition, priceOneGp, Number(targetGp ?? 0));
      const conditionMet: boolean = evaluateMultiplePriceCondition(rule.condition, comparisonGp);
      const action: IMultiplePriceGpAction = conditionMet ? rule.whenTrue : rule.whenFalse;

      if (!action || action.mode === 'none') {
        return results;
      }

      const ruleTargetGp: number = action.mode === 'fixed' ? action.value : priceOneGp + action.value;
      const rawInclusive: number = calculateInclusivePriceForGp(latestCost, ruleTargetGp, vatPercentage);
      const banded: number = action.priceBanding ? snapPriceToPriceBands(round(rawInclusive, 2), priceBands ?? []) : rawInclusive;
      const inclusive: number = round(banded, 2);
      const exclusive: number = round(inclusive / (1 + vatPercentage / 100), 6);
      const inclusiveField: string = getInclusivePriceField(rule.priceIndex);

      results.push({
        conditionMet,
        effectiveGp: round(calculateGPValue(vatPercentage, inclusive, latestCost), 2),
        exclusive,
        exclusiveField: getExclusivePriceField(rule.priceIndex),
        inclusive,
        inclusiveField,
        mode: action.mode,
        previousInclusive: round(previousPrices?.[inclusiveField] ?? 0, 2),
        priceBandingApplied: !!action.priceBanding && inclusive !== round(rawInclusive, 2),
        priceIndex: rule.priceIndex,
        targetGp: round(Math.min(ruleTargetGp, MAX_GP_PERCENTAGE), 2),
      });

      return results;
    }, []);
}

/**
 * Writes the derived prices onto a copy of a stock item so grid rows and update payloads stay in sync.
 */
export function applyMultiplePriceChangesToStockItem<ObjectType>(
  item: ObjectType,
  settings: IMultiplePriceChangeRulesStoreSettings | undefined,
  vatRates: VatRates,
  priceBands?: IPriceBand[],
): {item: ObjectType; results: IMultiplePriceChangeResult[]} {
  const results: IMultiplePriceChangeResult[] = calculateMultiplePriceChanges({
    latestCost: Number(item?.['latestCost'] ?? 0),
    previousPrices: resolvePreviousPrices(item),
    priceBands,
    sellPriceIncluding1: Number(item?.['sellPriIncl1'] ?? 0),
    settings,
    targetGp: Number(item?.['recommendedGP'] ?? 0),
    vatKey: Number(item?.['vatR'] ?? 0),
    vatRates,
  });

  if (results.length === 0) {
    return {item, results};
  }

  // Only the inclusive field is written: it is the field tracked for edits and sent to IQ, which
  // recalculates the exclusive value itself. The exclusive value stays on the result for display.
  const updated: ObjectType = {...item};
  results.forEach((result: IMultiplePriceChangeResult): void => {
    updated[result.inclusiveField] = result.inclusive;
  });

  return {item: updated, results};
}

/**
 * Puts derived sell prices 2-10 back to the values stored before the edit.
 * Used when retail price 1 is reverted so an earlier rule result is not kept.
 */
export function restoreMultiplePriceChangeFieldsToOriginal<ObjectType>(item: ObjectType): ObjectType {
  const originalValue = item?.['originalValue'] as Record<string, {value: unknown}> | undefined;
  if (!originalValue) {
    return item;
  }

  const restored: ObjectType = {...item};
  getMultiplePriceChangeInclusiveFields().forEach((field: string): void => {
    const original = originalValue[field];
    if (original && original.value !== undefined) {
      restored[field] = original.value;
    }
  });
  return restored;
}

/**
 * Returns true when any derived price on the item differs from its original value.
 */
export function stockItemHasMultiplePriceChanges(item: Record<string, unknown> | undefined): boolean {
  if (!item) {
    return false;
  }
  const originalValue = item['originalValue'] as Record<string, {value: unknown}> | undefined;

  return getMultiplePriceChangeInclusiveFields().some((field: string): boolean => {
    if (item[field] === undefined || item[field] === null) {
      return false;
    }
    return Number(item[field]) !== Number(originalValue?.[field]?.value ?? 0);
  });
}

/**
 * Prices as they were before the edit, so the preview can show "from → to".
 * Falls back to the current value when the item was loaded without an original snapshot.
 */
function resolvePreviousPrices<ObjectType>(item: ObjectType): Record<string, number> {
  const originalValue = item?.['originalValue'] as Record<string, {value: unknown}> | undefined;

  return getMultiplePriceChangeFields().reduce((previous: Record<string, number>, field: string) => {
    const original = originalValue?.[field]?.value;
    previous[field] = Number(original ?? item?.[field] ?? 0);
    return previous;
  }, {});
}

function normalizeAction(action: IMultiplePriceGpAction | undefined): IMultiplePriceGpAction {
  return {
    mode: action?.mode ?? 'none',
    priceBanding: !!action?.priceBanding,
    value: Number(action?.value ?? 0),
  };
}

function normalizeCondition(condition: IMultiplePriceCondition | undefined): IMultiplePriceCondition {
  return {
    operator: condition?.operator ?? 'always',
    source: condition?.source ?? 'priceOneGp',
    value: Number(condition?.value ?? 0),
  };
}

function round(value: number, decimals: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const factor: number = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
